// crewbit-admin/src/components/schedule/DayCanvasWithRouteToggle.tsx
//
// Schedule Module Phase C C4 — admin Day-view enhancement. Top half stays as
// the existing <DayCanvasContainer> (or DayCanvas via children); bottom half
// swaps to <RouteMap> when route toggle active. Per spec §5.3.
//
// Caller (admin schedule index) decides initial routeVisible based on URL
// zoom/lens state.

import { useEffect, useState } from 'react';
import { admin } from '../../styles/tokens';
import { useRouteRibbonSummary } from '../../hooks/useRouteRibbonSummary';
import { RouteMap } from './RouteMap';
import { RouteSummary } from './RouteSummary';
import { RouteToggleButton } from './RouteToggleButton';

export interface DayCanvasWithRouteToggleProps {
  businessId: string | undefined;
  effectiveAnchor: Date;
  /** The Day-view content to render in the top half (typically <DayCanvasContainer>). */
  children: React.ReactNode;
  /** Initial toggle state. Caller derives from URL view param. */
  initialRouteVisible?: boolean;
  /** Stop tap handler — surface up to caller for detail panel. */
  onStopTap?: (entryId: string) => void;
  onRouteVisibilityChange?: (visible: boolean) => void;
}

export function DayCanvasWithRouteToggle({
  businessId,
  effectiveAnchor,
  children,
  initialRouteVisible = false,
  onStopTap,
  onRouteVisibilityChange,
}: DayCanvasWithRouteToggleProps) {
  const [routeVisible, setRouteVisible] = useState(initialRouteVisible);
  const { summary, stops } = useRouteRibbonSummary(businessId, effectiveAnchor);
  useEffect(() => {
    setRouteVisible(initialRouteVisible);
  }, [initialRouteVisible]);

  const handleToggleRoute = () => {
    const next = !routeVisible;
    setRouteVisible(next);
    onRouteVisibilityChange?.(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
      <div style={{ flex: routeVisible ? 1 : 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {children}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 16px',
        borderTop: `1px solid ${admin.border}`,
        borderBottom: routeVisible ? `1px solid ${admin.border}` : 'none',
        backgroundColor: admin.surface,
      }}>
        <RouteToggleButton active={routeVisible} onToggle={handleToggleRoute} />
        {routeVisible ? <RouteSummary summary={summary} density="compact" /> : null}
      </div>

      {routeVisible ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 320 }}>
          <RouteMap
            stops={stops}
            onStopTap={(id) => { if (onStopTap) onStopTap(id); }}
          />
        </div>
      ) : null}
    </div>
  );
}
